// sfc32 PRNG with named, independently seeded streams (docs/08 §5).
// State is a plain tuple so it serializes into the save as-is.

export type RngState = [number, number, number, number];

export const STREAMS = ['reels', 'fate', 'draft', 'shop', 'events', 'bonus', 'plinko', 'dialogue'] as const;
export type StreamName = (typeof STREAMS)[number];
export type RngStates = Record<StreamName, RngState>;

/** cyrb128 string hash → 4×32-bit seed. */
export function hashSeed(str: string): RngState {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

/** A mutable view over an RngState tuple. Advancing it mutates the tuple in place. */
export class Rng {
  constructor(readonly s: RngState) {}

  static fromSeed(seed: string): Rng {
    const r = new Rng(hashSeed(seed));
    for (let i = 0; i < 12; i++) r.nextU32(); // warm-up
    return r;
  }

  nextU32(): number {
    const s = this.s;
    let a = s[0],
      b = s[1],
      c = s[2],
      d = s[3];
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    s[0] = a >>> 0;
    s[1] = b >>> 0;
    s[2] = c >>> 0;
    s[3] = d >>> 0;
    return t >>> 0;
  }

  /** Float in [0, 1). */
  next(): number {
    return this.nextU32() / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    if (n <= 0) return 0;
    return Math.floor(this.next() * n);
  }

  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    return min + this.int(max - min + 1);
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('pick from empty array');
    return arr[this.int(arr.length)]!;
  }

  /** Weighted pick; weights ≤ 0 are skipped. Returns -1 if all weights are 0. */
  weighted(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) if (w > 0) total += w;
    if (total <= 0) return -1;
    let x = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i]!;
      if (w <= 0) continue;
      x -= w;
      if (x < 0) return i;
    }
    return weights.length - 1;
  }

  /** Fisher–Yates, in place. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = arr[i]!;
      arr[i] = arr[j]!;
      arr[j] = tmp;
    }
    return arr;
  }
}

export function createStreams(runSeed: string): RngStates {
  const out = {} as RngStates;
  for (const name of STREAMS) out[name] = Rng.fromSeed(`${runSeed}:${name}`).s;
  return out;
}

export function cloneStreams(s: RngStates): RngStates {
  const out = {} as RngStates;
  for (const name of STREAMS) out[name] = [...s[name]] as RngState;
  return out;
}

/** A stream bound to state that lives inside a RunState (mutates it). */
export function stream(states: RngStates, name: StreamName): Rng {
  return new Rng(states[name]);
}

/** A throwaway generator from a clone of a stream (dry-runs must never advance real streams). */
export function peekStream(states: RngStates, name: StreamName): Rng {
  return new Rng([...states[name]] as RngState);
}

/**
 * Stateless deterministic roll for per-cell randomness (e.g. Dice values) that must be identical
 * across dry-runs, nudges and the real spin without advancing any stream.
 */
export function cellRoll(seed: string, spinIndex: number, reel: number, row: number, uid: number): Rng {
  const r = new Rng(hashSeed(`${seed}|${spinIndex}|${reel}|${row}|${uid}`));
  r.nextU32();
  return r;
}
